import { useEffect, useMemo, useRef, useState } from 'react'
import { ImageOverlay, MapContainer, Marker, useMapEvents } from 'react-leaflet'
import Const from 'constants/constant'
import { CRS, LatLngTuple } from 'leaflet'
import {
  rotatedMarkerViewIcon,
  smallNumberIcon
} from 'pages/building/floor/area/CustomMarker'
import { useRecoilState, useRecoilValue } from 'recoil'
import { floorDetailState, listPointState } from 'store/buildingStore'
import { currentPovState, povPerPointState } from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import { Point } from 'types/building'
import { LeafletRef } from 'types/leafletMap'
import Krpano from 'utils/krpano'

import useViewBuildingDetail from '../hooks/useViewBuildingDetail'

type Props = {
  refetchFloor: any
}

export default function ViewerFloorMap({ refetchFloor }: Props) {
  const mapContainerRef = useRef<any>(null)

  const [imgBounds, setImgBounds] = useState<LatLngTuple>([0, 0])
  const [radarAngle, setRadarAngle] = useState<number | null>(null)
  const [radarReady, setRadarReady] = useState(false)

  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [, setCurrentPov] = useRecoilState(currentPovState)
  const [, setPovPerPoint] = useRecoilState(povPerPointState)
  const points = useRecoilValue(listPointState)
  const floorDetail = useRecoilValue(floorDetailState)

  useEffect(() => {
    const updateRadarAngle = () => {
      const viewer = document.getElementById('embedpano-viewer')
      if (viewer) {
        const hlookat = (viewer as any)?.get('view.hlookat')
        if (typeof hlookat === 'number') {
          setRadarAngle(hlookat)
        }
      }
    }

    const interval = setInterval(updateRadarAngle, 100)
    return () => clearInterval(interval)
  }, [])

  // Store last point to reset radar immediately during render, preventing 1-frame flashes
  const prevPointRef = useRef(currentViewer.selectedPoint)
  if (prevPointRef.current !== currentViewer.selectedPoint) {
    setRadarReady(false)
    setRadarAngle(null)
    prevPointRef.current = currentViewer.selectedPoint
  }

  // Effect: Show radar only after pointDetail has been fetched AND the 1-second
  // Krpano BLEND animation has finished physically transitioning the scene.
  useEffect(() => {
    if (
      currentViewer.selectedPoint &&
      currentViewer.pointDetail?.id === currentViewer.selectedPoint
    ) {
      const timeout = setTimeout(() => {
        setRadarReady(true)
      }, 1000)
      return () => clearTimeout(timeout)
    }
  }, [currentViewer.selectedPoint, currentViewer.pointDetail?.id])

  const leafletRef = useRef<LeafletRef>({
    mapContainerRef: null,
    povMarkerRef: null,
    radarDragFlag: {
      isRadarMode: false,
      isDragging: false,
      rotate: null,
      hgap: null,
      mouseHoldTime: null
    }
  })

  const imagePath = useMemo(
    () => floorDetail?.mapFile?.s3Path,
    [floorDetail?.mapFile?.s3Path]
  )

  const { mapOnReady } = useViewBuildingDetail({
    imagePath,
    refetchFloor
  })

  const markerEventHandler = (point: Point) => {
    return {
      click() {
        // Reset currentPov so it won't reuse the previous navigation's POV
        setCurrentPov(null)
        // Also clear stored POV for the target point so viewer falls to initView
        setPovPerPoint((prev) => {
          const next = { ...prev }
          delete next[point.id]
          return next
        })
        setCurrentViewer((prev) => ({
          ...prev,
          selectedImage: point.image360?.id,
          selectedArea: `${point.areaId}`,
          selectedPoint: point.id,
          // Treat as left-menu selection so embedpano will reset POV to default
          isSelectPointInLeftMenu: true,
          isClickToDirection: false,
          isClickToNextFloor: false
        }))

        // Update URL
        const urlToUpdate = new URL(window.location.href)
        urlToUpdate.searchParams.set('point', point.id)
        urlToUpdate.searchParams.set('area', String(point.areaId))
        urlToUpdate.searchParams.delete('subimage')
        urlToUpdate.searchParams.delete('hlookat')
        urlToUpdate.searchParams.delete('vlookat')
        urlToUpdate.searchParams.delete('fov')
        window.history.pushState({}, '', urlToUpdate.toString())
      }
    }
  }

  const displayRadar = useMemo(() => {
    if (!radarReady) return null
    const radarPoint = points.find(
      (point) => point.id === currentViewer.selectedPoint
    )

    if (radarPoint && radarPoint.image360) {
      const rotate =
        radarAngle !== null
          ? Krpano.NormalizeHGap(radarAngle)
          : radarPoint.initView.h

      return (
        <Marker
          position={[radarPoint.y, radarPoint.x]}
          ref={(el) => {
            leafletRef.current.povMarkerRef = el
          }}
          zIndexOffset={430 + points.length}
          icon={rotatedMarkerViewIcon(
            rotate + (radarPoint.initView.radarHGap || 0)
          )}
          draggable={false}
        />
      )
    }
    return null
  }, [radarReady, currentViewer.selectedPoint, points, radarAngle])

  function MapEventHandler() {
    useMapEvents({
      click() {
        setCurrentViewer((prev) => ({ ...prev, isOpenMiniMap: true }))
      }
    })
    return null
  }

  if (!imagePath) return null

  return (
    <div className="p-[10px] h-44">
      <MapContainer
        ref={mapContainerRef}
        center={[-99999, -99999]}
        zoom={0}
        zoomSnap={0.1}
        zoomDelta={0.5}
        className="area-map-container theme-bg-third"
        crs={CRS.Simple}
        zoomControl={false}
        attributionControl={false}
        preferCanvas
        whenReady={() => mapOnReady(mapContainerRef, setImgBounds)}
      >
        {/* handle map event */}
        <MapEventHandler />

        {points?.map((point) => (
          <Marker
            key={`marker ${point.id}`}
            draggable={false}
            bubblingMouseEvents={false}
            eventHandlers={markerEventHandler(point)}
            zIndexOffset={
              point.id === currentViewer.selectedPoint ? 450 : 400 + point.order
            }
            // leaflet position is latlng with y=lat x=lng
            position={[point.y, point.x]}
            icon={smallNumberIcon(point, currentViewer.selectedPoint, true)}
            opacity={Const.OPACITY_OF_POINT_MAP}
          />
        ))}

        {displayRadar}

        <ImageOverlay url={imagePath} bounds={[[0, 0], imgBounds]} />
      </MapContainer>
    </div>
  )
}
